'use client';
import { Fragment, ReactNode, useRef, useState } from 'react';
import type { LinkSceneProps, Model } from './LinkScene';
import { useLenis } from '../../../LenisScroll';
import LinkScene from './LinkScene';
import { ScrollScene } from '../../ThreeLayout/ScrollScene';
import { TitleSection } from '../../../TitleSection';
import { useResetCanvasZIndex, useSetCanvasZIndex } from '../../ThreeLayout';
import { Button } from '../../../Inputs/Button';
import CloseIcon from '../../../_assets/icons/close.svg';
import ArrowLeftIcon from '../../../_assets/icons/arrowLeft.svg';
import ArrowRightIcon from '../../../_assets/icons/arrowRight.svg';
import { PaginationItem } from '../../../CardCarousel';
import { AnimatePresence, motion } from 'motion/react';
import { Text } from '../../../Text';

export type ProductShowcaseProps = {
	label?: string;
	title?: string;
	products: { modelName: Model; title?: string; overline?: string; copy?: ReactNode }[];
	learnMore?: {
		learn: string;
		more: string;
	};
};

export function ProductShowcase(props: ProductShowcaseProps) {
	const sections = useRef<LinkSceneProps['sections']>({
		carousel: useRef(null),
		modal: useRef(null),
		dockHover: useRef(null),
		headsetHover: useRef(null),
	}).current;

	const lenis = useLenis();

	const { productModels, activeProduct, setNewProductByDirection, setNewProductByName, resetActiveProduct } = useActiveProduct(
		props.products,
	);

	if (lenis && !activeProduct && lenis.isStopped) lenis.start();
	if (lenis && activeProduct && !lenis.isStopped) lenis.stop();

	const overlines = {
		headset: convertStringToReactNodes('4x Headsets\n& Headbands'),
		dock: convertStringToReactNodes('Charging Dock \nwith detachable Hub'),
	};

	props.products.forEach(product => {
		if (product.modelName === 'Headset' && product.overline) overlines.headset = convertStringToReactNodes(product.overline);
		if (product.modelName === 'Dock' && product.overline) overlines.dock = convertStringToReactNodes(product.overline);
	});

	return (
		<div data-component='ProductShowCase' className='relative'>
			<div className='relative container-xl  py-10 md:py-20'>
				{props.title && <TitleSection overline={props.label} title={props.title} className='z-60 relative' />}
				<div ref={sections.carousel}>
					<div
						ref={sections.dockHover}
						style={{
							position: 'absolute',
							top: '0',
							left: '0',
							width: '100%',
							height: '100%',
							zIndex: 60,
							pointerEvents: 'none',
							cursor: 'pointer',
						}}
					/>
					<div
						ref={sections.headsetHover}
						style={{
							position: 'absolute',
							top: '0',
							left: '0',
							width: '100%',
							height: '100%',
							zIndex: 60,
							pointerEvents: 'none',
							cursor: 'pointer',
						}}
					/>
					<Modal
						products={props.products}
						activeProduct={activeProduct}
						setNewProductByDirection={setNewProductByDirection}
						setNewProductByName={setNewProductByName}
						resetActiveProduct={resetActiveProduct}
						sections={sections}
					/>
					<div className='relative aspect-10/6'>
						<ScrollScene sceneAspectRatio={{ width: 10, height: 6 }}>
							{sceneProps => (
								<LinkScene
									sections={sections}
									modelsInfo={productModels}
									setNewProductByName={setNewProductByName}
									overlines={overlines}
									learnMore={props.learnMore}
									{...sceneProps}
								/>
							)}
						</ScrollScene>
					</div>
				</div>
			</div>
			<div
				className={`pointer-events-none absolute top-0 right-0 w-screen bg-no-repeat bg-cover md:after:content-[""] md:after:absolute md:after:top-0 md:after:left-0 md:after:w-screen md:after:h-32 md:after:bg-linear-to-t md:after:from-background-primary/0 md:after:to-background-primary max-md:-top-[7rem] max-md:aspect-375/1580 max-md:bg-product-showcase-bg-mobile md:-top-[10rem] md:aspect-1440/3000 md:bg-product-showcase-bg-desktop`}
			/>
		</div>
	);
}

function Modal({
	products,
	activeProduct,
	setNewProductByDirection,
	setNewProductByName,
	resetActiveProduct,
	sections,
}: {
	products: ProductShowcaseProps['products'];
	activeProduct: ProductShowcaseProps['products'][0] | undefined;
	sections: LinkSceneProps['sections'];
	setNewProductByDirection(direction: 'previous' | 'next'): void;
	setNewProductByName(model: Model): void;
	resetActiveProduct(): void;
}) {
	return (
		<div
			ref={sections.modal}
			data-three-js-layout='modal'
			className='top-0 left-0 w-[100dvw] h-[100dvh] transition-opacity ease-smooth duration-fast bg-background-primary p-10 flex-col justify-between fixed flex z-60'
			style={{ opacity: activeProduct ? 1 : 0, pointerEvents: activeProduct ? 'all' : 'none' }}
			inert={activeProduct ? undefined : true}
		>
			<div className='flex justify-end'>
				<Button parent='modal' styleType='secondary' styleSize='large' iconOnly onClick={() => resetActiveProduct()}>
					<CloseIcon />
				</Button>
			</div>

			<AnimatePresence>
				<div className='flex flex-col landscape:flex-row items-center w-full h-full'>
					<div className='landscape:w-1/2 h-1/2 landscape:h-full' />
					{activeProduct && (
						<motion.div
							key={activeProduct.title}
							className='landscape:w-1/2 py-10 max-w-96'
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ delay: 0.3, duration: 0.6 }}
						>
							<Text as='h3' styleType='heading-lg'>
								{activeProduct.title}
							</Text>
							<div className='pt-3'>{activeProduct.copy}</div>
						</motion.div>
					)}
				</div>
			</AnimatePresence>
			<div className='flex justify-between'>
				<div className='flex items-center gap-4'>
					<Button parent='modal' styleType='secondary' styleSize='large' iconOnly onClick={() => setNewProductByDirection('previous')}>
						<ArrowLeftIcon />
					</Button>
					<Button parent='modal' styleType='secondary' styleSize='large' iconOnly onClick={() => setNewProductByDirection('next')}>
						<ArrowRightIcon />
					</Button>
				</div>
				<div className='hidden md:flex flex-row justify-center'>
					{activeProduct &&
						products.map((product, index) => {
							return (
								<PaginationItem
									key={`modal-pagination-${index}`}
									active={product.modelName === activeProduct.modelName}
									onClick={() => setNewProductByName(product.modelName)}
								/>
							);
						})}
				</div>
				<div />
			</div>
		</div>
	);
}

export type ModalModelsStatus = {
	activeProduct: Model | undefined;
	products:
		| {
				[key in Model]: {
					position: number;
				};
		  }
		| undefined;
};

function useActiveProduct(products: ProductShowcaseProps['products']) {
	const [activeProduct, setActiveProduct] = useState<ProductShowcaseProps['products'][0] | undefined>(undefined);
	const [activeProductIndex, setActiveProductIndex] = useState<number | undefined>(undefined);
	const setZIndex = useSetCanvasZIndex();
	const resetZIndex = useResetCanvasZIndex();

	const productModels = useRef<ModalModelsStatus>({
		activeProduct: undefined,
		products: (() => {
			const object: ModalModelsStatus['products'] | {} = {};
			const modelsPosition = adjustArray([...Array(products.length).keys()], 0);

			for (let i = 0; i < modelsPosition.length; i++) {
				object[products[i].modelName] = {
					position: modelsPosition[i],
				};
			}
			return Object.keys(object).length > 0 ? (object as ModalModelsStatus['products']) : undefined;
		})(),
	}).current;

	return {
		productModels,
		activeProduct,
		setNewProductByDirection: (direction: 'previous' | 'next') => {
			let newActiveProductIndex = activeProductIndex;

			if (direction === 'previous' && activeProductIndex !== undefined) {
				if (activeProductIndex === 0) {
					newActiveProductIndex = products.length - 1;
				} else {
					newActiveProductIndex = activeProductIndex - 1;
				}
			}
			if (direction === 'next' && activeProductIndex !== undefined) {
				if (activeProductIndex === products.length - 1) {
					newActiveProductIndex = 0;
				} else {
					newActiveProductIndex = activeProductIndex + 1;
				}
			}

			if (newActiveProductIndex !== undefined) {
				setActiveProductIndex(newActiveProductIndex);
				setActiveProduct(products[newActiveProductIndex]);

				productModels.activeProduct = products[newActiveProductIndex].modelName;

				if (productModels.products) {
					const newModelsPosition = adjustArray([...Array(products.length).keys()], newActiveProductIndex);

					for (let i = 0; i < newModelsPosition.length; i++) {
						productModels.products[products[i].modelName] = {
							position: newModelsPosition[i],
						};
					}
				}
			}
		},
		setNewProductByName: (name: Model) => {
			setZIndex(60);
			const newActiveProductIndex = products.findIndex(product => product.modelName === name);
			if (newActiveProductIndex !== undefined) {
				setActiveProductIndex(newActiveProductIndex);
				setActiveProduct(products[newActiveProductIndex]);

				productModels.activeProduct = products[newActiveProductIndex].modelName;

				if (productModels.products) {
					const newModelsPosition = adjustArray([...Array(products.length).keys()], newActiveProductIndex);

					for (let i = 0; i < newModelsPosition.length; i++) {
						productModels.products[products[i].modelName] = {
							position: newModelsPosition[i],
						};
					}
				}
			}
		},
		resetActiveProduct: () => {
			setTimeout(() => {
				setActiveProductIndex(undefined);
				setActiveProduct(undefined);
			}, 150);
			setTimeout(() => {
				resetZIndex();
			}, 300);
			productModels.activeProduct = undefined;
		},
	};
}

function adjustArray(array: number[], active: number): number[] {
	const centerIndex = Math.floor(array.length / 2);

	const positionArray: number[] = [];
	for (let i = array.length, j = centerIndex * -1; i > 0; i--, j++) {
		positionArray.push(j);
	}
	const difference = centerIndex - active;
	const adjustedStart = array.slice(0, difference);
	const adjustedEnd = array.slice(difference, array.length);
	const newArray = [...adjustedEnd, ...adjustedStart];

	const newPositionArray: number[] = [];
	for (let i = 0; i < newArray.length; i++) {
		newPositionArray[i] = positionArray[newArray[i]];
	}

	return newPositionArray;
}

function convertStringToReactNodes(stringWithLineBreaks: string) {
	const parts = stringWithLineBreaks.split('\n');
	return parts.map((part, index) => (
		<Fragment key={`${index}-${part?.slice(0, 10)}`}>
			{part}
			{index !== parts.length - 1 && <br />}
		</Fragment>
	));
}
